import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Shield, ChevronRight, Loader2, HelpCircle, ExternalLink } from "lucide-react";
import { useWallet } from "@/contexts/WalletContext";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

// DuckDuckGo's icon CDN reliably serves high-quality favicons of any domain
const DDG = (domain) => `https://icons.duckduckgo.com/ip3/${domain}.ico`;

const WALLETS = [
  {
    id: "metamask",
    name: "MetaMask",
    desc: "Most popular browser & mobile wallet",
    logo: "https://upload.wikimedia.org/wikipedia/commons/3/36/MetaMask_Fox.svg",
    fallbackBg: "#F6851B",
    fallbackText: "M",
    deeplink: (host, path) => `https://metamask.app.link/dapp/${host}${path}`,
    detect: (eth) => eth?.isMetaMask && !eth?.isTrust && !eth?.isCoinbaseWallet,
  },
  {
    id: "trust",
    name: "Trust Wallet",
    desc: "Mobile & in-app browser",
    logo: DDG("trustwallet.com"),
    fallbackBg: "#3375BB",
    fallbackText: "T",
    deeplink: (host, path) => `https://link.trustwallet.com/open_url?coin_id=20000714&url=${encodeURIComponent("https://" + host + path)}`,
    detect: (eth) => eth?.isTrust || eth?.isTrustWallet,
  },
  {
    id: "binance",
    name: "Binance Wallet",
    desc: "Official Binance Web3 wallet",
    logo: DDG("binance.com"),
    fallbackBg: "#F0B90B",
    fallbackText: "B",
    deeplink: (host, path) => `bnc://app.binance.com/cedefi/wallet-direct?url=${encodeURIComponent("https://" + host + path)}`,
    detect: (eth) => eth?.isBinance || eth?.isBinanceWallet,
  },
  {
    id: "okx",
    name: "OKX Wallet",
    desc: "Multi-chain crypto wallet",
    logo: DDG("okx.com"),
    fallbackBg: "#000000",
    fallbackText: "O",
    deeplink: (host, path) => `okx://wallet/dapp/url?dappUrl=${encodeURIComponent("https://" + host + path)}`,
    detect: (eth) => eth?.isOkxWallet || eth?.isOKExWallet,
  },
  {
    id: "bitget",
    name: "Bitget Wallet",
    desc: "Web3 trading wallet",
    logo: DDG("web3.bitget.com"),
    fallbackBg: "#54FFE9",
    fallbackText: "B",
    deeplink: (host, path) => `bitkeep://bkconnect?action=dapp&url=${encodeURIComponent("https://" + host + path)}`,
    detect: (eth) => eth?.isBitKeep || eth?.isBitget,
  },
  {
    id: "tokenpocket",
    name: "TokenPocket",
    desc: "Multi-chain DeFi wallet",
    logo: DDG("tokenpocket.pro"),
    fallbackBg: "#2980FE",
    fallbackText: "TP",
    deeplink: (host, path) => `tpoutside://open?params=${encodeURIComponent(JSON.stringify({ dappUrl: "https://" + host + path, chain: "BSC" }))}`,
    detect: (eth) => eth?.isTokenPocket,
  },
  {
    id: "imtoken",
    name: "imToken",
    desc: "Secure mobile crypto wallet",
    logo: DDG("token.im"),
    fallbackBg: "#11C4D1",
    fallbackText: "im",
    deeplink: (host, path) => `imtokenv2://navigate/DappView?url=${encodeURIComponent("https://" + host + path)}`,
    detect: (eth) => eth?.isImToken,
  },
  {
    id: "coinbase",
    name: "Coinbase Wallet",
    desc: "Self-custody crypto wallet",
    logo: "https://upload.wikimedia.org/wikipedia/commons/1/1a/Coinbase.svg",
    fallbackBg: "#0052FF",
    fallbackText: "C",
    deeplink: (host, path) => `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent("https://" + host + path)}`,
    detect: (eth) => eth?.isCoinbaseWallet,
  },
  {
    id: "safepal",
    name: "SafePal",
    desc: "Hardware + software wallet",
    logo: DDG("safepal.com"),
    fallbackBg: "#4A21EF",
    fallbackText: "SP",
    deeplink: (host, path) => `https://link.safepal.io/open_url?url=${encodeURIComponent("https://" + host + path)}`,
    detect: (eth) => eth?.isSafePal || eth?.isSafePalWallet,
  },
  {
    id: "mathwallet",
    name: "MathWallet",
    desc: "Multi-platform crypto wallet",
    logo: DDG("mathwallet.org"),
    fallbackBg: "#3A6BFF",
    fallbackText: "M",
    deeplink: (host, path) => `mathwallet://?action=dapp&url=${encodeURIComponent("https://" + host + path)}`,
    detect: (eth) => eth?.isMathWallet,
  },
  {
    id: "walletconnect",
    name: "Other Wallets",
    desc: "Connect via in-app browser",
    logo: "https://avatars.githubusercontent.com/u/37784886?s=200&v=4",
    fallbackBg: "#3B99FC",
    fallbackText: "WC",
    deeplink: null,
    detect: () => false,
  },
];

function isMobile() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

export function WalletConnectModal({ open, onOpenChange, referrer, mode = "login" }) {
  const wallet = useWallet();
  const auth = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [imgError, setImgError] = useState({});
  const hasReferrer = !!(referrer && referrer.trim());

  const onImgError = (id) => setImgError(p => ({ ...p, [id]: true }));

  const handle = async (w) => {
    const eth = typeof window !== "undefined" ? window.ethereum : null;
    setBusy(w.id);

    try {
      // If any injected provider is present (user inside wallet's in-app browser),
      // use it directly regardless of which specific wallet they clicked.
      if (eth) {
        const conn = await wallet.connectInjected(w.id);
        await auth.authenticate(referrer, conn); // pass fresh data to avoid React state race
        onOpenChange(false);
        navigate("/dashboard");
        return;
      }

      // No injected provider → use deeplink (mobile) or guidance (desktop)
      if (w.deeplink) {
        const host = window.location.host;
        const path = window.location.pathname + window.location.search;
        const link = w.deeplink(host, path);

        if (isMobile()) {
          // Open the wallet app's in-app browser with our URL
          toast.message(`Opening ${w.name}…`, { description: "If it doesn't open, install the app and try again." });
          window.location.href = link;
        } else {
          toast.message(`Open in ${w.name}`, {
            description: "Install this wallet's mobile app and open the link from there, or use MetaMask browser extension.",
            duration: 6000,
          });
        }
        return;
      }

      // "Other Wallets" fallback when no injected and no specific deeplink
      toast.message("Open in wallet browser", {
        description: "Copy this URL and open it inside MetaMask/Trust/OKX in-app browser to connect.",
        duration: 6000,
      });
    } catch (e) {
      // Detect "Observation mode" / "watch-only" / "Account not found" type errors
      const msg = (e?.message || e?.shortMessage || e?.reason || "").toString().toLowerCase();
      if (msg.includes("observation") || msg.includes("watch") || msg.includes("read only") || msg.includes("read-only")) {
        toast.error("Wallet is in Watch-only mode", {
          description: "Import your wallet with seed phrase / private key (not just address) to enable signing.",
          duration: 8000,
        });
      }
      // Other errors are already surfaced by contexts
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="wallet-connect-modal"
        className="bg-[var(--surface-2)] border border-white/10 text-[var(--text-1)] sm:max-w-[480px] max-h-[90vh] overflow-y-auto p-0"
      >
        <DialogHeader className="px-5 pt-5">
          <div className="flex items-center justify-between">
            <HelpCircle size={16} className="text-[var(--text-3)] cursor-pointer hover:text-[var(--text-2)]" />
            <DialogTitle className="text-base font-semibold">
              {mode === "signup" ? "Sign Up · Connect Wallet" : "Login · Connect Wallet"}
            </DialogTitle>
            <div className="w-4" />
          </div>
          <DialogDescription className="text-[var(--text-3)] text-xs text-center mt-2">
            Sign a gasless message to log in. We support all major BSC wallets.
          </DialogDescription>
        </DialogHeader>

        {/* Referrer status banner (signup mode) */}
        {mode === "signup" && (
          <div
            className="mx-3 mt-2 mb-1 p-2.5 rounded-lg border flex items-start gap-2"
            data-testid="modal-referrer-banner"
            style={{
              background: hasReferrer
                ? "rgba(34,197,94,0.08)"
                : "rgba(248,113,113,0.10)",
              borderColor: hasReferrer
                ? "rgba(34,197,94,0.30)"
                : "rgba(248,113,113,0.35)",
            }}
          >
            <div
              className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0"
              style={{
                background: hasReferrer ? "#22C55E" : "#F87171",
                boxShadow: `0 0 6px ${hasReferrer ? "#22C55E" : "#F87171"}`,
              }}
            />
            <div className="text-[11px] leading-tight text-[var(--text-2)]">
              {hasReferrer ? (
                <>
                  <span className="font-semibold" style={{ color: "#22C55E" }}>Referral active:</span>{" "}
                  <span className="mono opacity-90">{referrer.slice(0, 8)}…{referrer.slice(-6)}</span>
                </>
              ) : (
                <>
                  <span className="font-semibold" style={{ color: "#F87171" }}>Referral required:</span>{" "}
                  Sign up only allowed via a sponsor's referral link.
                </>
              )}
            </div>
          </div>
        )}

        {/* Watch-only mode warning banner */}
        <div className="mx-3 mt-2 mb-1 p-2.5 rounded-lg border flex items-start gap-2"
          style={{
            background: "rgba(245,158,11,0.08)",
            borderColor: "rgba(245,158,11,0.30)",
          }}
        >
          <div className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ background: "#F59E0B", boxShadow: "0 0 6px #F59E0B" }} />
          <div className="text-[11px] leading-tight text-[var(--text-2)]">
            <span className="font-semibold text-[#F59E0B]">Heads up:</span>{" "}
            Wallet must be imported with seed phrase / private key, not just observation (watch-only) mode — otherwise signing will fail.
          </div>
        </div>

        <div className="px-3 pb-4 pt-3">
          <div className="flex flex-col gap-1.5">
            {WALLETS.map((w) => {
              const isBusy = busy === w.id;
              const useFallback = imgError[w.id];
              return (
                <button
                  key={w.id}
                  data-testid={`wallet-connect-${w.id}-button`}
                  onClick={() => handle(w)}
                  disabled={busy !== null}
                  className="flex items-center gap-3 px-3 py-3 rounded-xl border border-transparent hover:bg-[var(--surface-1)] hover:border-white/10 active:bg-[var(--surface-3)] transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed text-left group"
                >
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center overflow-hidden shrink-0"
                    style={{
                      background: useFallback ? w.fallbackBg : "#FFFFFF",
                      boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), 0 2px 6px rgba(0,0,0,0.18)",
                      border: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    {useFallback ? (
                      <span className="font-bold text-white text-base">{w.fallbackText}</span>
                    ) : (
                      <img
                        src={w.logo}
                        alt={w.name}
                        onError={() => onImgError(w.id)}
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{w.name}</div>
                    <div className="text-[11px] text-[var(--text-3)] truncate">{w.desc}</div>
                  </div>
                  {isBusy ? (
                    <Loader2 size={16} className="animate-spin text-[var(--purple-300)] shrink-0" />
                  ) : (
                    <ChevronRight size={16} className="text-[var(--text-3)] group-hover:text-[var(--text-2)] shrink-0" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-4 flex items-center justify-center gap-2 text-[10px] text-[var(--text-3)] uppercase tracking-[0.14em]">
            <Shield size={11} className="text-[var(--success)]" />
            <span>Network: BSC Mainnet · Chain 56</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
